import { LanguageCode } from '@heidi/schema/ml-scribe';

import { SupportedLocale } from './supported-locales';

/**
 * App UI locale → dictation input {@link LanguageCode}. Shared by dev tools and
 * onboarding.
 *
 * Built on first use rather than at module scope, and reached only through
 * {@link supportedLocaleToDictationLanguage}. `LanguageCode` is a **string enum**,
 * so spelling the table out eagerly would read enum members the moment this
 * module is imported — and this module sits behind the shared `@heidi/utils/i18n`
 * door, which pulls it into every i18n consumer. Any jest suite that partially
 * mocks `@heidi/schema/ml-scribe` (21 of them do, typically stubbing only
 * `SubscriptionTier`) leaves `LanguageCode` undefined, and an eager table then
 * fails the whole suite at import time with `Cannot read properties of undefined`.
 * Deferring the reads keeps the module inert on import, so the shared door stays
 * safe for consumers that never touch dictation.
 */
let dictationLanguageByLocale: Partial<Record<SupportedLocale, LanguageCode>> | undefined;

const getDictationLanguageByLocale = (): Partial<Record<SupportedLocale, LanguageCode>> =>
  (dictationLanguageByLocale ??= {
    [SupportedLocale.ArSa]: LanguageCode.ar_SA,
    [SupportedLocale.CaEs]: LanguageCode.ca,
    [SupportedLocale.DaDk]: LanguageCode.da,
    [SupportedLocale.DeDe]: LanguageCode.de,
    [SupportedLocale.En]: LanguageCode.en,
    [SupportedLocale.EnPs]: LanguageCode.en,
    [SupportedLocale.EsEs]: LanguageCode.es,
    [SupportedLocale.FiFi]: LanguageCode.fi,
    [SupportedLocale.FrFr]: LanguageCode.fr,
    [SupportedLocale.ItIt]: LanguageCode.it,
    [SupportedLocale.JaJp]: LanguageCode.ja,
    [SupportedLocale.KoKr]: LanguageCode.ko,
    [SupportedLocale.NlNl]: LanguageCode.nl,
    [SupportedLocale.NbNo]: LanguageCode.no,
    [SupportedLocale.PlPl]: LanguageCode.pl,
    [SupportedLocale.PtPt]: LanguageCode.pt,
    [SupportedLocale.SvSe]: LanguageCode.sv,
    [SupportedLocale.ZhCn]: LanguageCode.zh,
    [SupportedLocale.ZhTw]: LanguageCode.zh_Hant,
  });

export function supportedLocaleToDictationLanguage(
  locale: SupportedLocale
): LanguageCode | undefined {
  return getDictationLanguageByLocale()[locale];
}
